import { useState } from 'react';
import moment from 'moment';
import { useQuery } from 'react-query';
import { CalendarHeader } from './CalendarHeader';
import { ModesManager } from './modes/Manager';
import { CALENDAR_MODE__MONTH } from '../../constants';
import axios from 'axios';
import leavePolicyApi from '../../network/leavePolicy';
import { holidaysToEvents } from '../../utils/holidayEvents';
import { useLeaveTypes } from '../../hooks/useLeaveTypes';

const { REACT_APP_API_HOST } = process.env;

export function CalendarRoute() {
  const [mode, setMode] = useState(CALENDAR_MODE__MONTH);
  const [today, setToday] = useState(moment());
  const [person, setPerson] = useState<any>();
  const [office, setOffice] = useState<any>();
  const [isMyEvents, setIsMyEvents] = useState<boolean>(false);
  const [includePending, setIncludePending] = useState<boolean>(false);

  const { isFetched, data: response } = useQuery({
    queryKey: [
      'home-calendar',
      person?.id || 0,
      office?.id || 0,
      isMyEvents,
      includePending,
    ],
    queryFn: async () => {
      const { data } = await axios({
        method: 'post',
        url: `${REACT_APP_API_HOST}/v1/calendar`,
        params: {
          ...(person && person?.id ? { person: person.id } : {}),
          ...(office && office?.id && office.id !== '0' ? { office: office.id } : {}),
          ...(isMyEvents ? { myEvents: isMyEvents } : {}),
          ...(includePending ? { includePending: includePending } : {}),
        },
      });

      return data;
    },
    staleTime: 300000,
    cacheTime: 900000,
  });

  // Public holidays for the visible month (plus the overlapping weeks),
  // for the selected office when one is chosen.
  const holidayFrom = today.clone().startOf('month').subtract(7, 'days').format('YYYY-MM-DD');
  const holidayTo = today.clone().endOf('month').add(14, 'days').format('YYYY-MM-DD');
  const officeId = office && office?.id && office.id !== '0' ? String(office.id) : undefined;
  const { data: holidays = [] } = useQuery(
    ['calendar-holidays', holidayFrom, holidayTo, officeId ?? 'all'],
    () =>
      leavePolicyApi.getHolidays({
        from: holidayFrom,
        to: holidayTo,
        ...(officeId ? { office: officeId } : {}),
      }),
    { staleTime: 300000, retry: false },
  );

  // The API labels the built-in types; a company's own types arrive as
  // their key, so show their name instead.
  const { leaveTypes } = useLeaveTypes({ includeInactive: true });
  const leaveEvents = (response?.time_away || []).map((event: any) => {
    const custom = leaveTypes.find(
      (type) => !type.is_default && type.key === event?.type,
    );
    return custom
      ? {
          ...event,
          type: custom.name,
          title: String(event.title ?? '').replace(
            ` - ${custom.key}`,
            ` - ${custom.name}`,
          ),
        }
      : event;
  });

  function handlePrevious() {
    setToday((prev) =>
      prev.clone().subtract(1, mode as moment.unitOfTime.DurationConstructor),
    );
  }

  function handleToday() {
    setToday(moment());
  }

  function handleNext() {
    setToday((prev) =>
      prev.clone().add(1, mode as moment.unitOfTime.DurationConstructor),
    );
  }

  return (
    <div className="flex overflow-hidden rounded-lg bg-white shadow lg:flex-1">
      <div className="flex-1 lg:flex lg:flex-col">
        <CalendarHeader
          today={today}
          mode={mode}
          setMode={setMode}
          onPrevious={handlePrevious}
          onToday={handleToday}
          onNext={handleNext}
          setPerson={setPerson}
          setOffice={setOffice}
          setIsMyEvents={setIsMyEvents}
          isMyEvents={isMyEvents}
          setIncludePending={setIncludePending}
          includePending={includePending}
        />
        {isFetched && (
          <ModesManager
            mode={mode}
            events={[...holidaysToEvents(holidays), ...leaveEvents]}
            today={today}
          />
        )}
      </div>
    </div>
  );
}
